import React, { useState } from "react";
import { Button, Row, SeparatorList, TextInput } from "@narmi/design_system";

import ContextForm from "../ContextForm";
import InternationalPhoneTextInput from "../InternationalPhoneTextInput";
import { useNotificationContext } from "../NotificationContext";
import { useLoadingContext } from "../LoadingContext";
import PhoneTextInput from "../PhoneTextInput";

const VERIFICATION_STATES = Object.freeze({
  REQUEST_CODE: "request_code",
  SUBMIT_CODE: "submit_code",
  COMPLETE: "complete",
});

type VerificationState = (typeof VERIFICATION_STATES)[keyof typeof VERIFICATION_STATES];

/**
 * Shape of the errors thrown by the `onRequestCode` / `onVerifyCode` callbacks.
 * Every member is optional — callers throw API error payloads, which vary.
 */
interface MfaDeviceError {
  id?: string;
  seed?: string;
  message?: string;
  phone_number?: string[];
  non_field_errors?: string[];
}

/** Field errors handed back to the enclosing ContextForm, keyed by field name. */
type SubmitCallback = (errors?: MfaDeviceError | Record<string, unknown>) => void;

export interface AddPhoneMfaDeviceProps {
  onCancel?: () => void;
  onRequestCode: () => Promise<unknown>;
  onVerifyCode: (seed: string) => Promise<unknown>;
  onUseADifferentPhoneNumber: () => void;
  isInternationalSupported?: boolean;
  label?: string;
  countryCodeLabel?: string;
  labelVerificationCode?: string;
  labelCancel?: string;
  labelVerify?: string;
  labelResendCode?: string;
  labelDifferentPhone?: string;
  /** Form field name consumed by ContextForm.Field (cloneElement contract) */
  field?: string;
  consentSlot?: React.ReactNode;
  submitDisabled?: boolean;
}

type InternationalPhoneMfaFieldProps = {
  field?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  error?: string;
  countryCodeLabel: string;
  label: string;
};

const InternationalPhoneMfaField = ({
  field: _field,
  value,
  onChange,
  onBlur,
  error,
  countryCodeLabel,
  label,
}: InternationalPhoneMfaFieldProps) => (
  <InternationalPhoneTextInput
    value={value}
    onChange={(nextValue: string) => onChange?.(nextValue)}
    onBlur={(nextValue: string) => onBlur?.(nextValue)}
    error={error}
    countryCodeLabel={countryCodeLabel}
    phoneNumberLabel={label}
    phoneNumberTestId="phone_number"
    countryCodeTestId="phone_number_country_code"
  />
);

const AddPhoneMfaDevice = ({
  onCancel,
  onRequestCode,
  onVerifyCode,
  onUseADifferentPhoneNumber,
  isInternationalSupported = false,
  label = "Phone number",
  countryCodeLabel = "Country code",
  labelVerificationCode = "Verification code",
  labelCancel = "Cancel",
  labelVerify = "Verify",
  labelResendCode = "Resend code",
  labelDifferentPhone = "Use a different phone number",
  field,
  consentSlot = null,
  submitDisabled = false,
}: AddPhoneMfaDeviceProps) => {
  const { sendNotification } = useNotificationContext();
  const [verificationState, setVerificationState] = useState<VerificationState>(
    VERIFICATION_STATES.REQUEST_CODE
  );
  const [seed, setSeed] = useState("");
  const { setIsLoading } = useLoadingContext();

  const submitHandler = async (
    callback: SubmitCallback | null,
    verificationState: VerificationState
  ) => {
    const callbackOrNull: SubmitCallback = (err) => (callback ? callback(err) : null);
    if (verificationState === VERIFICATION_STATES.REQUEST_CODE) {
      try {
        await onRequestCode();
      } catch (caught) {
        const err = caught as MfaDeviceError;
        if (err.id === "two_factor_authentication_code_requested" && err.seed) {
          setSeed(err.seed);
          setVerificationState(VERIFICATION_STATES.SUBMIT_CODE);
          sendNotification({
            type: "info",
            text: err.message ?? "",
          });
          callbackOrNull();
        } else if (err?.phone_number?.[0]) {
          callbackOrNull({ [String(field)]: { phone_number: err.phone_number[0] } });
        } else {
          callbackOrNull(err);
        }
      }
    } else if (verificationState === VERIFICATION_STATES.SUBMIT_CODE) {
      try {
        await onVerifyCode(seed);
        setVerificationState(VERIFICATION_STATES.COMPLETE);
        callbackOrNull();
      } catch (caught) {
        const err = caught as MfaDeviceError;
        if (err.non_field_errors?.[0]) {
          callbackOrNull({ [String(field)]: { token: err.non_field_errors?.[0] } });
        } else {
          callbackOrNull(err);
        }
      }
    }
  };

  const renderVerificationInput = () => {
    const inputStates: VerificationState[] = [
      VERIFICATION_STATES.REQUEST_CODE,
      VERIFICATION_STATES.SUBMIT_CODE,
    ];
    if (!inputStates.includes(verificationState)) return null;
    let inputComponent = null;
    if (verificationState === VERIFICATION_STATES.REQUEST_CODE) {
      inputComponent = (
        <ContextForm.Field required key="phone_number">
          {isInternationalSupported ? (
            <InternationalPhoneMfaField
              field="phone_number"
              countryCodeLabel={countryCodeLabel}
              label={label}
            />
          ) : (
            <PhoneTextInput label={label} field="phone_number" />
          )}
        </ContextForm.Field>
      );
    } else {
      inputComponent = (
        <ContextForm.Field required key="token">
          <TextInput field="token" label={labelVerificationCode} />
        </ContextForm.Field>
      );
    }
    const isRequestCodeStep = verificationState === VERIFICATION_STATES.REQUEST_CODE;
    return (
      <div className="add-phone-mfa-device-row">
        <Row>
          <Row.Item>
            <div>{inputComponent}</div>
          </Row.Item>
          <Row.Item shrink>
            <div className="mfa-button-wrapper">
              {onCancel && (
                <Button kind="negative" label={labelCancel} onClick={onCancel} type="button" />
              )}
              <ContextForm.Action
                onSubmit={(callback: SubmitCallback) => {
                  submitHandler(callback, verificationState);
                }}
              >
                <Button
                  label={labelVerify}
                  testId="verify-mfa-device-button"
                  disabled={isRequestCodeStep && submitDisabled}
                />
              </ContextForm.Action>
            </div>
          </Row.Item>
        </Row>
        {isRequestCodeStep && consentSlot}
      </div>
    );
  };

  const resendCodeHandler = () => {
    setIsLoading(true);
    setVerificationState(VERIFICATION_STATES.SUBMIT_CODE);
    submitHandler(null, VERIFICATION_STATES.REQUEST_CODE).finally(() => setIsLoading(false));
  };

  const useADifferentNumberHandler = () => {
    setVerificationState(VERIFICATION_STATES.REQUEST_CODE);
    onUseADifferentPhoneNumber();
  };

  const renderFooterButtons = () => {
    if (verificationState !== VERIFICATION_STATES.SUBMIT_CODE) return null;
    return (
      <div className="margin--top--s">
        <SeparatorList
          items={[
            <Button
              key="button-resend"
              label={labelResendCode}
              kind="plain"
              type="button"
              size="s"
              onClick={() => resendCodeHandler()}
            />,
            <Button
              key="button-different-phone"
              label={labelDifferentPhone}
              kind="plain"
              type="button"
              size="s"
              onClick={useADifferentNumberHandler}
            />,
          ]}
        />
      </div>
    );
  };

  return (
    <>
      {renderVerificationInput()}
      {renderFooterButtons()}
    </>
  );
};

export default AddPhoneMfaDevice;
